import type { ChatMessage } from '@/app/KeywordTracker/types/aiModalTypes';
import Avatar from '@/components/common/Avatar';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import Flex from '@/components/common/Flex';
import classNames from 'classnames';
import { useState } from 'react';
import { MdCheck, MdContentCopy } from 'react-icons/md';
import { AI_ASSISTANT_AVATAR, AI_SENDER_NAME } from '../../constants';
import { formatChatTimestamp } from '../../utils/formatChatTimestamp';
import AiMessage from './AiMessage';

interface ChatMessageItemProps {
  message: ChatMessage;
}

const getOwnerMessageStyle = () => {
  const themeVariant = document.documentElement.getAttribute('data-theme-variant');

  if (themeVariant === 'primary' || themeVariant === 'yellow') {
    return { color: 'black' };
  }

  return { color: 'white' };
};

const ChatMessageItem = ({ message }: ChatMessageItemProps) => {
  const { id, type, body, timestamp, sender } = message;
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const isOwner = type === 'user';
  const formattedTime = formatChatTimestamp(timestamp);

  const handleCopy = () => {
    navigator.clipboard.writeText(body?.message || '').then(() => {
      setCopiedId(id);
      setTimeout(() => {
        setCopiedId(null);
      }, 2000);
    });
  };

  const copyButton = (
    <li
      className="list-inline-item cursor-pointer chat-option-hover"
      onClick={handleCopy}
    >
      <ChildrensTooltip placement="top" html="کپی" delayToRemove={0}>
        <div>
          {copiedId === id ? (
            <MdCheck width={12} />
          ) : (
            <MdContentCopy className="d-inline-block" width={12} />
          )}
        </div>
      </ChildrensTooltip>
    </li>
  );

  return (
    <div id={String(id)} key={id}>
      <Flex className="p-1 pb-3 mx-2">
        <div
          className={classNames('flex-1 mx-2', {
            'd-flex justify-content-end': !isOwner
          })}
          style={{ maxWidth: '100%', marginTop: '-2px' }}
        >
          <div
            style={{
              width: '95%',
              maxWidth: '1200px',
              minWidth: '300px'
            }}
          >
            <Flex
              alignItems="center"
              className={classNames('hover-actions-trigger', {
                'flex-end-center': !isOwner,
                'align-items-end': isOwner
              })}
            >
              <div
                className={classNames('p-1 rounded-2 chat-message', {
                  'bg-200': !isOwner,
                  'bg-primary': isOwner
                })}
                style={isOwner ? getOwnerMessageStyle() : {}}
              >
                <AiMessage message={body?.message} />
              </div>
            </Flex>
            <Flex
              className={classNames('hover-actions-trigger', {
                'flex-end-center': !isOwner,
                'align-items-center': isOwner
              })}
            >
              <div className="text-400 gap-1 d-flex fs--2">
                {!isOwner && (
                  <ul className="position-relative list-inline mb-0 text-400 mx-2">
                    {copyButton}
                  </ul>
                )}
                <span className="font-weight-semi-bold">
                  {sender?.name || (isOwner ? 'شما' : AI_SENDER_NAME)}
                </span>
                <span>-</span>
                {formattedTime}
                {isOwner && (
                  <ul className="position-relative list-inline mb-0 text-400 mx-2">
                    {copyButton}
                  </ul>
                )}
              </div>
            </Flex>
          </div>
        </div>

        {!isOwner && <Avatar src={AI_ASSISTANT_AVATAR} />}
      </Flex>
    </div>
  );
};

export default ChatMessageItem;
